import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  LayoutDashboard, Calendar, Image as ImageIcon, Radio, Users, 
  CreditCard, Plus, Trash2, Edit, Upload, Eye, CheckCircle, Clock, XCircle
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Upload state
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadCategory, setUploadCategory] = useState('');
  const [uploadClientCode, setUploadClientCode] = useState('');
  const [uploadEventCode, setUploadEventCode] = useState('');
  const [uploading, setUploading] = useState(false);

  // Event state
  const [eventOpen, setEventOpen] = useState(false);
  const [newEventName, setNewEventName] = useState('');
  const [creatingEvent, setCreatingEvent] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, bookingsRes, photosRes, eventsRes] = await Promise.all([
        axios.get(`${API}/api/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/api/bookings`, { withCredentials: true }),
        axios.get(`${API}/api/photos`, { withCredentials: true }),
        axios.get(`${API}/api/events`, { withCredentials: true })
      ]);
      
      setStats(statsRes.data);
      setBookings(bookingsRes.data);
      setPhotos(photosRes.data);
      setEvents(eventsRes.data);
    } catch (e) {
      console.error('Error fetching data:', e);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadPhoto = async () => {
    if (!uploadFile || !uploadCategory) {
      toast.error('Please select a file and category');
      return;
    }

    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      
      let url = `${API}/api/photos/upload?category=${uploadCategory}`;
      if (uploadClientCode) url += `&client_code=${uploadClientCode.toUpperCase()}`;
      if (uploadEventCode) url += `&event_code=${uploadEventCode.toUpperCase()}`;
      
      await axios.post(url, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Photo uploaded successfully!');
      setUploadOpen(false);
      setUploadFile(null);
      setUploadCategory('');
      setUploadClientCode('');
      setUploadEventCode('');
      fetchData();
    } catch (e) {
      console.error('Upload error:', e);
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;
    
    try {
      await axios.delete(`${API}/api/photos/${photoId}`, { withCredentials: true });
      toast.success('Photo deleted');
      fetchData();
    } catch (e) {
      toast.error('Failed to delete photo');
    }
  };

  const handleCreateEvent = async () => {
    if (!newEventName.trim()) {
      toast.error('Please enter an event name');
      return;
    }

    setCreatingEvent(true);
    
    try {
      const { data } = await axios.post(
        `${API}/api/events`,
        { event_name: newEventName },
        { withCredentials: true }
      );
      
      toast.success(`Event created! Code: ${data.event_code}`);
      setEventOpen(false);
      setNewEventName('');
      fetchData();
    } catch (e) {
      toast.error('Failed to create event');
    } finally {
      setCreatingEvent(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    try {
      await axios.delete(`${API}/api/events/${eventId}`, { withCredentials: true });
      toast.success('Event deleted');
      fetchData();
    } catch (e) {
      toast.error('Failed to delete event');
    }
  };

  const handleUpdateBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(
        `${API}/api/bookings/${bookingId}/status`,
        { payment_status: status },
        { withCredentials: true }
      );
      toast.success('Booking status updated');
      fetchData();
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;
    
    try {
      await axios.delete(`${API}/api/bookings/${bookingId}`, { withCredentials: true });
      toast.success('Booking deleted');
      fetchData();
    } catch (e) {
      toast.error('Failed to delete booking');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black pt-24 flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pt-24" data-testid="admin-dashboard">
      <div className="container-luxury py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-['Playfair_Display'] text-3xl text-white">Admin Dashboard</h1>
            <p className="text-gray-500 text-sm">Manage your photography business</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-[#1A1A1A] border border-[#333] p-1">
            <TabsTrigger value="overview" className="data-[state=active]:bg-[#C9A14A] data-[state=active]:text-black">
              <LayoutDashboard className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="bookings" className="data-[state=active]:bg-[#C9A14A] data-[state=active]:text-black">
              <Calendar className="w-4 h-4 mr-2" />
              Bookings
            </TabsTrigger>
            <TabsTrigger value="photos" className="data-[state=active]:bg-[#C9A14A] data-[state=active]:text-black">
              <ImageIcon className="w-4 h-4 mr-2" />
              Photos
            </TabsTrigger>
            <TabsTrigger value="events" className="data-[state=active]:bg-[#C9A14A] data-[state=active]:text-black">
              <Radio className="w-4 h-4 mr-2" />
              Events
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="card-luxury p-6" data-testid="stat-bookings">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#C9A14A]/10 rounded-full flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-[#C9A14A]" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Total Bookings</p>
                    <p className="text-2xl text-white font-semibold">{stats?.total_bookings || 0}</p>
                  </div>
                </div>
              </div>
              
              <div className="card-luxury p-6" data-testid="stat-paid">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-500/10 rounded-full flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-green-500" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Paid Bookings</p>
                    <p className="text-2xl text-white font-semibold">{stats?.paid_bookings || 0}</p>
                  </div>
                </div>
              </div>
              
              <div className="card-luxury p-6" data-testid="stat-photos">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-500/10 rounded-full flex items-center justify-center">
                    <ImageIcon className="w-6 h-6 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Total Photos</p>
                    <p className="text-2xl text-white font-semibold">{stats?.total_photos || 0}</p>
                  </div>
                </div>
              </div>
              
              <div className="card-luxury p-6" data-testid="stat-events">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-500/10 rounded-full flex items-center justify-center">
                    <Radio className="w-6 h-6 text-purple-500" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Active Events</p>
                    <p className="text-2xl text-white font-semibold">{stats?.total_events || 0}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Bookings */}
            <div className="card-luxury p-6">
              <h3 className="text-white text-lg mb-4">Recent Bookings</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#333]">
                      <th className="text-left text-gray-400 text-xs uppercase tracking-wider py-3">Client</th>
                      <th className="text-left text-gray-400 text-xs uppercase tracking-wider py-3">Date</th>
                      <th className="text-left text-gray-400 text-xs uppercase tracking-wider py-3">Session</th>
                      <th className="text-left text-gray-400 text-xs uppercase tracking-wider py-3">Code</th>
                      <th className="text-left text-gray-400 text-xs uppercase tracking-wider py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.slice(0, 5).map((booking) => (
                      <tr key={booking.id} className="border-b border-[#222]">
                        <td className="py-3 text-white">{booking.name}</td>
                        <td className="py-3 text-gray-400">{booking.date}</td>
                        <td className="py-3 text-gray-400">{booking.session_type}</td>
                        <td className="py-3 text-[#C9A14A]">{booking.confirmation_code}</td>
                        <td className="py-3">
                          <span className={`px-2 py-1 text-xs rounded ${
                            booking.payment_status === 'paid' 
                              ? 'bg-green-500/10 text-green-400' 
                              : 'bg-yellow-500/10 text-yellow-400'
                          }`}>
                            {booking.payment_status}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {bookings.length === 0 && (
                      <tr>
                        <td colSpan={5} className="py-8 text-center text-gray-500">No bookings yet</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings" className="space-y-4">
            <div className="card-luxury overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[#222]">
                      <th className="text-left text-gray-400 text-xs uppercase tracking-wider p-4">Client</th>
                      <th className="text-left text-gray-400 text-xs uppercase tracking-wider p-4">Contact</th>
                      <th className="text-left text-gray-400 text-xs uppercase tracking-wider p-4">Date/Time</th>
                      <th className="text-left text-gray-400 text-xs uppercase tracking-wider p-4">Session</th>
                      <th className="text-left text-gray-400 text-xs uppercase tracking-wider p-4">Code</th>
                      <th className="text-left text-gray-400 text-xs uppercase tracking-wider p-4">Status</th>
                      <th className="text-left text-gray-400 text-xs uppercase tracking-wider p-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((booking) => (
                      <tr key={booking.id} className="border-b border-[#222]" data-testid={`booking-row-${booking.id}`}>
                        <td className="p-4 text-white">{booking.name}</td>
                        <td className="p-4">
                          <div className="text-gray-400 text-sm">{booking.email}</div>
                          <div className="text-gray-500 text-xs">{booking.phone}</div>
                        </td>
                        <td className="p-4">
                          <div className="text-white text-sm">{booking.date}</div>
                          <div className="text-gray-500 text-xs">{booking.time}</div>
                        </td>
                        <td className="p-4 text-gray-400">{booking.session_type}</td>
                        <td className="p-4 text-[#C9A14A] font-mono">{booking.confirmation_code}</td>
                        <td className="p-4">
                          <Select
                            value={booking.payment_status}
                            onValueChange={(value) => handleUpdateBookingStatus(booking.id, value)}
                          >
                            <SelectTrigger className="w-28 bg-transparent border-[#333] text-white text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#1A1A1A] border-[#333]">
                              <SelectItem value="pending" className="text-yellow-400">Pending</SelectItem>
                              <SelectItem value="paid" className="text-green-400">Paid</SelectItem>
                              <SelectItem value="cancelled" className="text-red-400">Cancelled</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteBooking(booking.id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            data-testid={`delete-booking-${booking.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {bookings.length === 0 && (
                      <tr>
                        <td colSpan={7} className="p-8 text-center text-gray-500">No bookings yet</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Photos Tab */}
          <TabsContent value="photos" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-white text-lg">Photo Gallery</h3>
              <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-gold" data-testid="upload-photo-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Upload Photo
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#1A1A1A] border-[#333]">
                  <DialogHeader>
                    <DialogTitle className="text-white">Upload Photo</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label className="text-gray-400">Photo File</Label>
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setUploadFile(e.target.files[0])}
                        className="mt-2 bg-transparent border-[#333] text-white"
                        data-testid="photo-file-input"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-400">Category</Label>
                      <Select value={uploadCategory} onValueChange={setUploadCategory}>
                        <SelectTrigger className="mt-2 bg-transparent border-[#333] text-white">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-[#333]">
                          <SelectItem value="portraits" className="text-white">Portraits</SelectItem>
                          <SelectItem value="weddings" className="text-white">Weddings</SelectItem>
                          <SelectItem value="events" className="text-white">Events</SelectItem>
                          <SelectItem value="graduation" className="text-white">Graduation</SelectItem>
                          <SelectItem value="studio" className="text-white">Studio</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-gray-400">Client Code (optional)</Label>
                      <Input
                        value={uploadClientCode}
                        onChange={(e) => setUploadClientCode(e.target.value)}
                        placeholder="LVL-XXXX"
                        className="mt-2 input-luxury"
                        data-testid="client-code-input"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-400">Event Code (optional)</Label>
                      <Input
                        value={uploadEventCode}
                        onChange={(e) => setUploadEventCode(e.target.value)}
                        placeholder="EVT-XXXXXX"
                        className="mt-2 input-luxury"
                        data-testid="event-code-input"
                      />
                    </div>
                    <Button
                      onClick={handleUploadPhoto}
                      className="w-full btn-gold"
                      disabled={uploading}
                      data-testid="upload-submit-button"
                    >
                      {uploading ? 'Uploading...' : 'Upload Photo'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {photos.map((photo) => (
                <div key={photo.id} className="card-luxury group relative overflow-hidden" data-testid={`photo-item-${photo.id}`}>
                  <div className="aspect-square">
                    <img
                      src={`${API}${photo.image_url}`}
                      alt={photo.original_filename}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeletePhoto(photo.id)}
                      className="text-red-400 hover:bg-red-500/20"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="p-2 text-xs">
                    <div className="text-gray-400 truncate">{photo.category}</div>
                    {photo.client_code && <div className="text-[#C9A14A]">{photo.client_code}</div>}
                    {photo.event_code && <div className="text-purple-400">{photo.event_code}</div>}
                  </div>
                </div>
              ))}
              {photos.length === 0 && (
                <div className="col-span-full text-center py-12 text-gray-500">
                  No photos uploaded yet
                </div>
              )}
            </div>
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-white text-lg">Live Events</h3>
              <Dialog open={eventOpen} onOpenChange={setEventOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-gold" data-testid="create-event-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Event
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#1A1A1A] border-[#333]">
                  <DialogHeader>
                    <DialogTitle className="text-white">Create New Event</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label className="text-gray-400">Event Name</Label>
                      <Input
                        value={newEventName}
                        onChange={(e) => setNewEventName(e.target.value)}
                        placeholder="Wedding - Smith Family"
                        className="mt-2 input-luxury"
                        data-testid="event-name-input"
                      />
                    </div>
                    <Button
                      onClick={handleCreateEvent}
                      className="w-full btn-gold"
                      disabled={creatingEvent}
                      data-testid="create-event-submit"
                    >
                      {creatingEvent ? 'Creating...' : 'Create Event'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {events.map((event) => (
                <div key={event.id} className="card-luxury p-6" data-testid={`event-item-${event.id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-white font-medium">{event.event_name}</h4>
                      <p className="text-[#C9A14A] font-mono text-lg mt-1">{event.event_code}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteEvent(event.id)}
                      className="text-red-400 hover:bg-red-500/20"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="text-gray-500 text-xs">
                    Created: {new Date(event.created_at).toLocaleDateString()}
                  </div>
                </div>
              ))}
              {events.length === 0 && (
                <div className="col-span-full text-center py-12 text-gray-500">
                  No events created yet
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
